// src/pages/qbo/PreCashCard.tsx
//
// Pre-cutoff cash applied after the cutoff (agent 3.25.5). The office keeps
// editing receipts dated on/before the conversion cutoff and applying that
// cash to newer invoices. Advantage shows the invoices paid; in QuickBooks the
// cash sits in the placeholder customer (legacy cash journals) and the
// invoices stay open. Per customer the agent posts a PRECASH journal (Dr A/R
// placeholder / Cr A/R customer) and a zero-dollar payment applying it to the
// invoices, so they close exactly as in Advantage.

import { useState } from 'react';
import qboApi from '../../services/qboApi';
import SyncResultList from './SyncResultList';
import type { SyncResult } from './qboTypes';
import { money } from './qboTypes';
import { runJob, type JobView } from './qboJobs';

interface Props {
  onMessage: (type: 'error' | 'success' | 'info', text: string) => void;
  /** Customer codes the audit left unexplained — one click fills the box. */
  suggestedCodes?: number[];
}

interface PreCashDoc { invoice: string; qboId: string; date: string; total: number; qboBalance: number; adsBalance: number; amount: number; receipts: string }
interface PreCashCustomer { code: number; name: string; qboId: string; total: number; docs: PreCashDoc[]; skipped: string[]; error: string | null; fromOnAccount?: number; fromPlaceholder?: number; onAccountPosted?: number; onAccountNow?: number; existingJournal?: string | null }
interface PreCashPlan { cutoff: string; customers: PreCashCustomer[]; total: number; notes: string[] }

const td = 'px-2 py-0.5 border-b border-slate-800/60 text-xs';

export default function PreCashCard({ onMessage, suggestedCodes }: Props) {
  const [codesText, setCodesText] = useState('');
  const [plan, setPlan] = useState<PreCashPlan | null>(null);
  const [planning, setPlanning] = useState(false);
  const [running, setRunning] = useState(false);
  const [job, setJob] = useState<JobView | null>(null);
  const [result, setResult] = useState<SyncResult | null>(null);

  const parseCodes = (): number[] => [...new Set(codesText.split(/[\s,;]+/).map((s) => parseInt(s, 10)).filter((n) => n > 0))];

  const doPlan = async () => {
    const codes = parseCodes();
    if (codes.length === 0) { onMessage('error', 'Enter customer codes'); return; }
    setPlanning(true); setPlan(null); setResult(null);
    try {
      const p = (await qboApi.preCashPlan(codes)) as PreCashPlan;
      setPlan(p);
      const n = p.customers.filter((c) => c.total > 0).length;
      onMessage('info', `${n} customer(s) with pre-cutoff cash to apply, ${money(p.total)} in all`);
    } catch (e: unknown) { onMessage('error', (e as Error).message); }
    finally { setPlanning(false); }
  };

  const doRun = async () => {
    if (!plan) return;
    const codes = plan.customers.filter((c) => c.total > 0).map((c) => c.code);
    if (codes.length === 0) return;
    setRunning(true); setResult(null); setJob(null);
    try {
      const r = await runJob(() => qboApi.preCashRun({ codes }), setJob);
      setResult(r);
      onMessage(r.errors ? 'error' : 'success', `Pre-cutoff cash: ${r.created} customer(s) posted, ${r.skipped} skipped, ${r.errors} errors`);
      setPlan(null);
    } catch (e: unknown) { onMessage('error', (e as Error).message); }
    finally { setRunning(false); setJob(null); }
  };

  const runnable = plan ? plan.customers.filter((c) => c.total > 0) : [];

  return (
    <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
      <div className="flex flex-wrap items-center gap-3">
        <div className="mr-auto">
          <div className="text-sm font-semibold text-slate-400">Pre-cutoff cash applied after the cutoff</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Invoices Advantage shows paid by receipts dated inside the legacy cash journals, still open in QuickBooks because that cash sits in the placeholder. Per customer: journal PRECASH (Dr A/R placeholder / Cr A/R customer) + a zero-dollar payment applying it to the invoices.</div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <input className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200 w-72" placeholder="customer codes, e.g. 1241 8116 4309" value={codesText} onChange={(e) => setCodesText(e.target.value)} />
        {suggestedCodes && suggestedCodes.length > 0 && (
          <button className="px-2 py-1 rounded text-[11px] border border-slate-600 text-slate-300 hover:bg-slate-700" onClick={() => setCodesText(suggestedCodes.join(' '))}>use the audit's unexplained ({suggestedCodes.length})</button>
        )}
        <button className="px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-600 text-slate-200 hover:bg-slate-700 transition-colors disabled:opacity-50" onClick={doPlan} disabled={planning || running}>{planning ? 'Planning…' : 'Plan'}</button>
        {runnable.length > 0 && (
          <button className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-500 transition-colors disabled:opacity-50" onClick={doRun} disabled={running}>
            {running ? (job ? `Posting ${job.done}/${job.total}…` : 'Starting…') : `Post for ${runnable.length} customer(s) · ${money(plan!.total)}`}
          </button>
        )}
      </div>
      {plan && (
        <div className="mt-3 space-y-2">
          {plan.customers.map((c) => (
            <div key={c.code} className="border border-slate-700/60 rounded-lg p-2">
              <div className="text-xs text-slate-200 font-semibold">{c.code} {c.name} {c.error ? <span className="text-red-300 font-normal">— {c.error}</span> : <span className="text-slate-400 font-normal">— {c.total > 0 ? `${money(c.total)} to apply on ${c.docs.length} invoice(s)${(c.fromOnAccount ?? 0) > 0 ? ` · ${money(c.fromOnAccount!)} from the ONACCT credit (${money(c.onAccountPosted ?? 0)} posted, ${money(c.onAccountNow ?? 0)} still on account)` : ''}${(c.fromPlaceholder ?? 0) > 0 ? ` · ${money(c.fromPlaceholder!)} from the placeholder` : ''}${c.existingJournal ? ` · re-run of ${c.existingJournal}` : ''}` : 'nothing to apply'}</span>}</div>
              {c.docs.length > 0 && (
                <table className="w-full mt-1 border-collapse">
                  <thead><tr>{['Invoice', 'Date', 'Total', 'QBO open', 'ADS open', 'Apply', 'Paid in Advantage by'].map((h) => <th key={h} className="text-left px-2 py-1 border-b border-slate-700 text-slate-500 font-semibold text-[10px] uppercase">{h}</th>)}</tr></thead>
                  <tbody>{c.docs.map((d) => (
                    <tr key={d.invoice}><td className={td}>{d.invoice}</td><td className={td}>{d.date}</td><td className={td + ' text-right'}>{money(d.total)}</td><td className={td + ' text-right'}>{money(d.qboBalance)}</td><td className={td + ' text-right'}>{money(d.adsBalance)}</td><td className={td + ' text-right text-green-300'}>{money(d.amount)}</td><td className={td + ' text-slate-400'}>{d.receipts}</td></tr>
                  ))}</tbody>
                </table>
              )}
              {c.skipped.length > 0 && <ul className="mt-1 text-[11px] text-amber-200/80 list-disc pl-4">{c.skipped.map((s, i) => <li key={i}>{s}</li>)}</ul>}
            </div>
          ))}
          <ul className="text-[11px] text-slate-500 list-disc pl-4">{plan.notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
        </div>
      )}
      <SyncResultList result={result} title="Pre-cutoff cash" />
    </div>
  );
}
